// src/padplanner/PadPlannerGate.jsx
//
// The not-enabled state, shared by every PadPlanner page. S1's rule, kept:
// NOT ENTITLED IS A STATE OF THE PAGE, NOT A REDIRECT. A beforeLoad redirect
// would read the entitlement store before it has loaded, and bouncing staff off
// a path they typed is what router.barePaths.test.jsx refuses.
//
// It reads the same entitled-codes set TopNav filters its tab on, so the two
// cannot disagree, and it renders its children -- which make the requests --
// only when the answer is yes. The server refuses anyway: every
// /api/v1/padplanner/* route is gated on PADPLANNER.
//
// Every page renders through here, so the error boundary sits here too
// (PadPlannerErrorBoundary.jsx): a render error is the page's, not the app's.
//
// And so does the section strip (PadPlannerNav.jsx), on a page that names its
// `section`: only once the org is known to hold PadPlanner, never on the
// not-enabled state, and never on the editor, which names none. Its right end
// is given to the page (NavEndContext) for what the page puts there.
import { useState } from "react";
import PadPlannerErrorBoundary from "./PadPlannerErrorBoundary";
import PadPlannerNav, { NavEndContext } from "./PadPlannerNav";
import { useAnalyticsProductsStore } from "../stores/useAnalyticsProductsStore";
import { PADPLANNER_PRODUCT, PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export const NOT_ENABLED_TEXT = `${PADPLANNER_DISPLAY_NAME} isn't enabled for your organization.`;

export default function PadPlannerGate({ section, children }) {
  return <PadPlannerErrorBoundary><GateBody section={section}>{children}</GateBody></PadPlannerErrorBoundary>;
}

function GateBody({ section, children }) {
  const [navEnd, setNavEnd] = useState(null);
  const loaded = useAnalyticsProductsStore((s) => s.loaded);
  const entitled = useAnalyticsProductsStore((s) => s.productCodes.has(PADPLANNER_PRODUCT));

  // Nothing while entitlements load: either answer shown early can be wrong.
  if (!loaded) return null;
  if (!entitled) {
    return (
      <div className="flex h-full min-h-0 flex-col overflow-auto p-6 text-sm text-fg-secondary">
        {NOT_ENABLED_TEXT}
      </div>
    );
  }
  if (!section) return children;
  return (
    <div className="flex h-full min-h-0 flex-col">
      <PadPlannerNav section={section} endRef={setNavEnd} />
      <div className="min-h-0 flex-1"><NavEndContext.Provider value={navEnd}>{children}</NavEndContext.Provider></div>
    </div>
  );
}
